/**
 * chatDayGrouping — pure calendar-day grouping for the flat chat
 * timeline. Replaces the retired week/month bucket taxonomy
 * (historyBucketing.ts) with a single chronological stream: items
 * ascend oldest → newest and each local-tz calendar day gets one
 * group, rendered by TimelineView as a sticky day banner ("Today",
 * "Yesterday", weekday, "Jul 12, 2026") above that day's messages.
 *
 * Pure module — no React. ``now`` is an explicit parameter on every
 * label/grouping function (never ``new Date()`` inline) so tests pin
 * the clock and cross-midnight sessions re-derive from the caller's
 * single authoritative timestamp.
 */

import type { EventLogEntry } from "./eventLog";
import type { ChatMessage } from "./messageProjection";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * One renderable row of the unified timeline. Persisted history rows
 * carry a projected ``ChatMessage``; live rows carry the EventLog
 * entry itself (TimelineView projects + renders it with cursor /
 * replay affordances the persisted path doesn't need).
 */
export type TimelineItem =
  | { kind: "message"; at: number; key: string; message: ChatMessage }
  | {
      kind: "entry";
      at: number;
      key: string;
      entry: EventLogEntry;
      /** Index into the shell's chronological entries array — lets the
       *  view map the /replay cursor onto the right row. */
      entryIndex: number;
    };

/** One calendar day of timeline items, oldest day first in the list. */
export interface DayGroup {
  /** Local-tz midnight of the day. */
  date: Date;
  /** Sticky banner label — "Today" | "Yesterday" | weekday | "Jul 12, 2026". */
  label: string;
  /** Ascending ``at`` within the day; ties keep input order. */
  items: TimelineItem[];
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

/**
 * Group timeline items into local-tz calendar days, oldest day first,
 * items ascending by ``at`` within each day. Ties keep input order
 * (callers put persisted rows before live rows so restored copies of
 * a same-millisecond pair render in their original sequence).
 */
export function groupItemsByDay(items: readonly TimelineItem[], now: Date): DayGroup[] {
  const sorted = [...items].sort((a, b) => a.at - b.at);
  const groups: DayGroup[] = [];
  let current: DayGroup | null = null;
  for (const item of sorted) {
    const day = atMidnight(new Date(item.at));
    if (current === null || !sameDay(current.date, day)) {
      current = { date: day, label: formatDayLabel(day, now), items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Today" | "Yesterday" | "Tuesday" | "Mar 14, 2026" — banner label
 *  for a calendar day. Moved from HistoryBuckets.formatDayLabel with
 *  a "Today" branch added (the retired bucket UI rendered today via a
 *  separate divider; the flat stream banners every day uniformly).
 *
 *  ``now`` is an explicit parameter — see module header. Day distance
 *  is rounded (not exact-ms equality) so DST-shifted 23/25-hour days
 *  still land on the right label.
 */
export function formatDayLabel(date: Date, now: Date): string {
  const d = new Date(date);
  const oneDayMs = 24 * 60 * 60 * 1000;
  const dayDiff = Math.round((atMidnight(now).getTime() - atMidnight(d).getTime()) / oneDayMs);
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  // Within last 6 days → weekday name (e.g. "Thursday").
  if (dayDiff > 0 && dayDiff <= 6) return WEEKDAYS[d.getDay()];
  // Otherwise short date.
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// ---------------------------------------------------------------------------
// Internal — date math (local timezone, same conventions as the
// retired historyBucketing module)
// ---------------------------------------------------------------------------

/** Returns midnight (local) of the given Date. Doesn't mutate input. */
function atMidnight(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
